import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection, $isRangeSelection, $createParagraphNode,
  $getNodeByKey,
  COMMAND_PRIORITY_EDITOR, KEY_TAB_COMMAND,
} from 'lexical';
import { $insertNodeToNearestRoot } from '@lexical/utils';
import {
  INSERT_LAYOUT_COMMAND, UPDATE_LAYOUT_COMMAND,
  $createLayoutContainerNode, $createLayoutItemNode,
  $isLayoutContainerNode, $isLayoutItemNode,
  LayoutContainerNode, LayoutItemNode,
} from '../nodes/LayoutNode';

export default function LayoutPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([LayoutContainerNode, LayoutItemNode])) {
      throw new Error('LayoutPlugin: LayoutContainerNode and LayoutItemNode must be registered on the editor');
    }

    return editor.registerCommand(
      INSERT_LAYOUT_COMMAND,
      ({ templateColumns, columnCount }) => {
        editor.update(() => {
          const container = $createLayoutContainerNode(templateColumns);
          for (let i = 0; i < columnCount; i++) {
            const item = $createLayoutItemNode();
            const para = $createParagraphNode();
            item.append(para);
            container.append(item);
          }
          $insertNodeToNearestRoot(container);
          // Move selection into first column's first paragraph
          const first = container.getFirstChild()?.getFirstChild();
          if (first) first.select();
        });
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      UPDATE_LAYOUT_COMMAND,
      ({ templateColumns, columnCount, containerKey }) => {
        editor.update(() => {
          const container = $getNodeByKey(containerKey);
          if (!$isLayoutContainerNode(container)) return;

          container.setTemplateColumns(templateColumns);

          const existingItems = container.getChildren().filter($isLayoutItemNode);
          const currentCount = existingItems.length;

          if (columnCount > currentCount) {
            for (let i = currentCount; i < columnCount; i++) {
              const item = $createLayoutItemNode();
              item.append($createParagraphNode());
              container.append(item);
            }
          } else if (columnCount < currentCount) {
            // Remove extra columns from the end (move content to last kept column)
            for (let i = currentCount - 1; i >= columnCount; i--) {
              const extra = existingItems[i];
              const target = existingItems[columnCount - 1];
              extra.getChildren().forEach(child => target.append(child));
              extra.remove();
            }
          }
        });
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  // Tab / Shift+Tab: move between columns
  useEffect(() => {
    return editor.registerCommand(
      KEY_TAB_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;

        let node = selection.anchor.getNode();
        // Walk up to find a layout-item parent
        while (node !== null) {
          if ($isLayoutItemNode(node)) break;
          node = node.getParent?.();
        }
        if (!$isLayoutItemNode(node)) return false;

        const container = node.getParent();
        if (!$isLayoutContainerNode(container)) return false;

        const items = container.getChildren().filter($isLayoutItemNode);
        const idx = items.indexOf(node);

        event.preventDefault();

        const target = event.shiftKey
          ? items[idx - 1]   // Shift+Tab → previous column
          : items[idx + 1];  // Tab → next column

        if (target) {
          const firstChild = target.getFirstChild();
          if (firstChild) firstChild.select();
        } else if (!event.shiftKey) {
          // Tab past last column — move after the container
          const after = container.getNextSibling();
          if (after) {
            after.select?.() ?? after.getFirstChild?.()?.select?.();
          } else {
            // Append a paragraph after the container and move there
            editor.update(() => {
              const para = $createParagraphNode();
              container.insertAfter(para);
              para.select();
            });
          }
        }

        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  return null;
}
